import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CircleCheckIcon, CircleIcon } from "lucide-react";
import { ChannelStateChip } from "@/components/badge/channel-state";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { DATE_FULL } from "@/lib/date";

interface Channel {
  id: number;
  pid: string;
  name: string;
}

interface ChannelRelease {
  id: number;
  versionId: string;
  channelId: number;
  releasedAt: string;
  /** Staged-rollout percent (1–100) at this stage. */
  percent?: number | null;
  /** Channel-flow node this record stepped to (null on legacy rows). */
  state?: string | null;
  operatorUserId?: string | null;
  operatorName?: string;
}

// Read-only overview: each channel with this version's LATEST record (the
// walk's current position). All operations (stepping the flow, deleting a
// record, building packages) live on the channel detail page — one entry
// point, so the two views can't drift apart. Click a row to go there.
export const ChannelReleases = ({
  versionId,
  pid,
}: {
  versionId: string;
  pid: string;
}) => {
  const navigate = useNavigate();
  const { data: channels, isLoading } = useSWR<Channel[]>(
    swrKey("/channels", { pid })
  );
  const { data: releases } = useSWR<ChannelRelease[]>(
    swrKey("/channel-releases", { versionId })
  );
  // Server returns logs oldest→newest, so the last one per channel is the
  // current position of this version's walk on that channel.
  const releasesByChannel = new Map<number, ChannelRelease[]>();
  for (const r of releases ?? []) {
    const list = releasesByChannel.get(r.channelId);
    if (list) list.push(r);
    else releasesByChannel.set(r.channelId, [r]);
  }

  if (!channels || channels.length === 0) {
    return isLoading ? <ListLoading /> : <ListEmpty>No channels.</ListEmpty>;
  }

  return (
    <div className="flex flex-col gap-1">
      {channels.map((channel, i) => {
        const latest = releasesByChannel.get(channel.id)?.at(-1);
        return (
          <RowCard
            key={channel.id}
            onClick={() => navigate(`/apps/${pid}/channels/${channel.id}`)}
          >
            <RowIndex n={i + 1} />
            <span
              title={latest ? "Released" : "Not released"}
              className="flex shrink-0"
            >
              {latest ? (
                <CircleCheckIcon className="h-4 w-4 text-primary" />
              ) : (
                <CircleIcon className="h-4 w-4 text-muted-foreground" />
              )}
            </span>
            <span className="min-w-0 flex-1 truncate">{channel.name}</span>
            {latest && (
              <>
                {latest.state && <ChannelStateChip state={latest.state} />}
                {/* 百分比只在放量中有信息量 */}
                <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {latest.state === "rolling" && latest.percent != null
                    ? `${latest.percent}%`
                    : "—"}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {format(new Date(latest.releasedAt), DATE_FULL)}
                </span>
              </>
            )}
          </RowCard>
        );
      })}
    </div>
  );
};

export default ChannelReleases;
